import { supabase } from "./supabase";

/**
 * O IO da aba Cursos gratuitos (000381), a tela da equipe dos cursos gratuitos ao vivo.
 *
 * ZERO regra aqui. Quem decide se o curso pode ser salvo, se o endereço pode mudar, se a mudança
 * pede motivo e se o certificado sai é o BANCO, e a recusa vem com a frase pronta (`detalhe`) — a
 * regra de `lib/sugestoes.ts`. Nenhum `select` cru: as tabelas `cg_*` têm `revoke all`, e a leitura
 * sai pelas funções com o portão da aba dentro.
 */

export type Resposta = { ok: boolean; motivo?: string; detalhe?: string; pede_motivo?: boolean; nada_mudou?: boolean };

export type CursoDaLista = {
  slug: string;
  nome: string;
  carga_horaria: number;
  num_aulas: number;
  data: string;
  ativo: boolean;
  requer_presenca: boolean;
  exige_presenca_total: boolean;
  /** "certificados-cf" quando veio de lá pela carga; "hub" quando nasceu aqui. */
  origem: "hub" | "certificados-cf";
  aulas_com_agenda: number;
  presencas: number;
  inscritos: number;
  certificados: number;
  revogados: number;
};

export type AulaDoCurso = {
  numero: number;
  /** Nulos quando a aula não tem agenda: ela existe de todo jeito, de 1 ao número de aulas. */
  titulo: string | null;
  data: string | null;
  hora: string | null;
  presencas: number;
};

export type CertificadoDoCurso = {
  token: string;
  nome: string;
  email: string;
  emitido_em: string;
  revogado: boolean;
  /** De onde veio: a carga de lá, a página de emitir, ou a equipe à mão. */
  origem: "certificados-cf" | "pagina" | "equipe";
  /** O e-mail tem outro certificado no mesmo curso (os pares repetidos de lá). */
  repetido: boolean;
};

export type MudancaDoCurso = {
  em: string;
  alvo: "curso" | "agenda" | "certificado";
  alvo_ref: string;
  acao: string;
  antes: Record<string, unknown> | unknown[] | null;
  depois: Record<string, unknown> | unknown[] | null;
  motivo: string | null;
  por: string;
};

export type CursoCompleto = {
  ok: true;
  pode_editar: boolean;
  curso: Omit<CursoDaLista, "aulas_com_agenda" | "presencas" | "inscritos" | "certificados" | "revogados"> & {
    criado_em: string;
    editado_no_hub_em: string | null;
    agenda_editada_no_hub_em: string | null;
  };
  aulas: AulaDoCurso[];
  certificados: CertificadoDoCurso[];
  registro: MudancaDoCurso[];
};

export type Inscrito = {
  email: string;
  nome: string;
  telefone: string | null;
  aulas: number[];
  primeira: string;
  ultima: string;
  /** "valido" quando o e-mail tem certificado que vale; "revogado" quando só tem revogado. */
  certificado: "valido" | "revogado" | null;
};

export type DadosDoCurso = {
  slug: string;
  nome: string;
  carga_horaria: number | null;
  num_aulas: number | null;
  data: string;
  requer_presenca: boolean;
  exige_presenca_total: boolean;
};

export type AulaDaAgenda = { numero: number; titulo: string; data: string; hora: string };

async function chamar<T>(nome: string, args?: Record<string, unknown>): Promise<T | Resposta> {
  const { data, error } = await supabase.rpc(nome, args);
  if (error) return { ok: false, detalhe: error.message };
  return data as T | Resposta;
}

export function lerCursos() {
  return chamar<{ ok: true; pode_editar: boolean; cursos: CursoDaLista[] }>("cg_admin_cursos");
}

export function lerCurso(slug: string) {
  return chamar<CursoCompleto>("cg_admin_curso", { p_slug: slug });
}

export function lerInscritos(slug: string) {
  return chamar<{ ok: true; inscritos: Inscrito[] }>("cg_admin_inscritos", { p_slug: slug });
}

/** Cria (com `slugAtual` nulo) ou edita um curso. */
export function salvarCurso(slugAtual: string | null, d: DadosDoCurso, motivo: string) {
  return chamar<Resposta & { slug?: string }>("cg_admin_salvar_curso", {
    p_slug_atual: slugAtual,
    p_slug: d.slug,
    p_nome: d.nome,
    p_carga_horaria: d.carga_horaria,
    p_num_aulas: d.num_aulas,
    p_data: d.data || null,
    p_requer_presenca: d.requer_presenca,
    p_exige_presenca_total: d.exige_presenca_total,
    p_motivo: motivo.trim() || null,
  });
}

export function ativarCurso(slug: string, ativo: boolean, motivo: string) {
  return chamar<Resposta>("cg_admin_ativar_curso", { p_slug: slug, p_ativo: ativo, p_motivo: motivo.trim() || null });
}

export function salvarAgenda(slug: string, aulas: AulaDaAgenda[]) {
  return chamar<Resposta>("cg_admin_salvar_agenda", { p_slug: slug, p_aulas: aulas });
}

export function emitirAMao(slug: string, nome: string, email: string, motivo: string) {
  return chamar<Resposta & { token?: string; ja_emitido?: boolean }>("cg_admin_emitir", {
    p_slug: slug,
    p_nome: nome,
    p_email: email,
    p_motivo: motivo.trim() || null,
  });
}

export function revogarCertificado(token: string, revogar: boolean, motivo: string) {
  return chamar<Resposta>("cg_admin_revogar", { p_token: token, p_revogar: revogar, p_motivo: motivo.trim() || null });
}

/** A frase de reserva, para quando nem a RPC nem a rede responderam. */
export function frase(r: Resposta | null | undefined): string {
  if (!r) return "Não foi possível falar com o servidor.";
  if (r.detalhe) return r.detalhe;
  return r.ok ? "Feito." : "Não foi possível salvar.";
}

/** Os links públicos de um curso, no hub. */
export function linkDaPresenca(slug: string, aula: number): string {
  return `${window.location.origin}/certificados/p/${slug}/${aula}`;
}
export function linkDaEmissao(slug: string): string {
  return `${window.location.origin}/certificados/emitir/${slug}`;
}
export function linkDoCertificado(token: string): string {
  return `/certificados/${token}`;
}

/** O telefone guardado só com os dígitos, como a pessoa o lê. */
export function telefoneNaTela(t: string | null): string {
  if (!t) return "—";
  const m = /^(\d{2})(\d{4,5})(\d{4})$/.exec(t);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : t;
}
